// ABOUTME: Material-UI wizard component for setting up family profiles and children
// ABOUTME: Uses Stepper component for navigation and comprehensive form validation

import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useFamilyTerminology } from '../hooks/useFamilyTerminology';
import { monthNameToNumber } from '../utils/dateUtils';
import AvatarSelector from './AvatarSelector';
import RoleEditModal from './RoleEditModal';
import VideoPlayer from './VideoPlayer';
import NicknamesChipInput from './common/NicknamesChipInput';
import { GoalType, CreateGoalRequest } from '../types/api';
import { getCurrentAccessToken } from '../services/api';
import {
  Container,
  Paper,
  Stepper,
  Step,
  StepLabel,
  Box,
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Card,
  CircularProgress,
  Chip,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Avatar,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  Stack,
  Fade,
  Snackbar,
  Alert
} from '@mui/material';
import {
  StarRounded,
  FavoriteRounded,
  GroupsRounded,
  PersonAddRounded,
  DeleteRounded,
  NavigateNextRounded,
  NavigateBeforeRounded
} from '@mui/icons-material';

interface Child {
  name: string;
  aliases: string[];
  birthYear: number;
  birthMonth: string; // Changed to required string for month names
  birthDay?: number;
  gender?: string;
  profileImageUrl?: string;
}

interface Adult {
  email: string;
  firstName: string;
  lastName: string;
  role?: string;
  customMessage?: string;
}

interface UserProfile {
  role?: string;
  mantra?: string;
  familyLoveQuestion?: string;
  familyNeedQuestion?: string;
  profileImageUrl?: string;
}

interface DefaultGoal {
  title: string;
  description: string; // Internal description like "(2 day passes allowed)"
  type: GoalType;
  targetValue: number;
  dayPasses: number;
  isRecurring?: boolean;
  rewardDescription?: string; // User-entered reward text
}

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const FamilySetupWizard: React.FC = () => {
  const { Term, term } = useFamilyTerminology();
  const [currentStep, setCurrentStep] = useState(0); // Material-UI Stepper is 0-indexed
  const [familyName, setFamilyName] = useState('');
  const [familyDescription] = useState('');
  const [children, setChildren] = useState<Child[]>([]);
  const [adults, setAdults] = useState<Adult[]>([]);
  const [userProfile, setUserProfile] = useState<UserProfile>({});
  const [goals, setGoals] = useState<Map<string, DefaultGoal[]>>(new Map());
  const [currentChild, setCurrentChild] = useState<Child>({
    name: '',
    aliases: [],
    birthYear: 0, // No default year - user must select
    birthMonth: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showUserRoleModal, setShowUserRoleModal] = useState(false);
  const [showAdultRoleModal, setShowAdultRoleModal] = useState(false);
  const [editingAdultIndex, setEditingAdultIndex] = useState<number>(-1);
  const [showGoalEditModal, setShowGoalEditModal] = useState(false);
  const [editingGoal, setEditingGoal] = useState<{ childName: string; goalIndex: number; goal: DefaultGoal } | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });
  const [newGoal, setNewGoal] = useState<CreateGoalRequest>({
    childId: 0,
    title: '',
    type: GoalType.HeartStreak,
    targetValue: 5,
    reward: '',
    dayPasses: 1,
    isRecurring: false,
    heartStreakTemplate: {
      consecutiveDays: 5,
      dayPasses: 1
    },
    starCollectionTemplate: {
      totalStars: 10,
      timePeriodDays: 7
    },
    heartCollectionTemplate: {
      totalHearts: 10,
      timePeriodDays: 30
    }
  });
  
  const { user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1; // 1-12

  const handleChildYearChange = (newYear: number) => {
    const updated = { ...currentChild, birthYear: newYear };
    // Auto-clear birth month if switching to current year and month is now in the future
    if (newYear === currentYear && currentChild.birthMonth) {
      const monthNum = MONTH_NAMES.indexOf(currentChild.birthMonth) + 1;
      if (monthNum > currentMonth) {
        updated.birthMonth = '';
      }
    }
    setCurrentChild(updated);
  };

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbar({ open: true, message, severity });
  };

  const parseHeartStreakFromTitle = (title: string) => {
    const daysMatch = title.match(/Get hearts (\d+) days/);
    const squaresMatch = title.match(/\((\d+) (?:day passes|squares allowed)\)/);
    return {
      consecutiveDays: daysMatch ? parseInt(daysMatch[1]) : 5,
      dayPasses: squaresMatch ? parseInt(squaresMatch[1]) : 0
    };
  };

  const parseStarCollectionFromTitle = (title: string) => {
    const starsMatch = title.match(/Collect (\d+) stars/);
    const daysMatch = title.match(/in (\d+) days/);
    return {
      totalStars: starsMatch ? parseInt(starsMatch[1]) : 10,
      timePeriodDays: daysMatch ? parseInt(daysMatch[1]) : 0
    };
  };

  const parseHeartCollectionFromTitle = (title: string) => {
    const heartsMatch = title.match(/Collect (\d+) hearts/);
    const daysMatch = title.match(/in (\d+) days/);
    return {
      totalHearts: heartsMatch ? parseInt(heartsMatch[1]) : 10,
      timePeriodDays: daysMatch ? parseInt(daysMatch[1]) : 0
    };
  };

  // Helper function to generate goal titles (same logic as GoalsManager)
  const generateGoalTitle = (type: GoalType, targetValue: number, dayPasses: number, timePeriodDays?: number): string => {
    if (type === GoalType.HeartStreak) {
      const days = targetValue;
      const squares = dayPasses ?? 0;
      if (squares > 0) {
        const passText = squares === 1 ? 'day pass' : 'day passes';
        return `Get hearts ${days} days in a row (${squares} ${passText})`;
      } else {
        return `Get hearts ${days} days in a row`;
      }
    } else if (type === GoalType.StarCollection) {
      const stars = targetValue;
      const days = timePeriodDays || 0;
      
      if (days > 0) {
        return `Collect ${stars} stars in ${days} days`;
      } else {
        return `Collect ${stars} stars`;
      }
    } else if (type === GoalType.HeartCollection) {
      const hearts = targetValue;
      const days = timePeriodDays || 0;
      
      if (days > 0) {
        return `Collect ${hearts} hearts in ${days} days`;
      } else {
        return `Collect ${hearts} hearts`;
      }
    }
    return '';
  };

  // Default goals for each child (Issue #489) - using proper title generation
  
  const defaultGoals: DefaultGoal[] = [
    {
      title: generateGoalTitle(GoalType.HeartStreak, 3, 0),
      description: 'recurring (0 day passes)',
      type: GoalType.HeartStreak,
      targetValue: 3,
      dayPasses: 0,
      isRecurring: true,
      rewardDescription: 'favorite snack'
    },
    {
      title: generateGoalTitle(GoalType.HeartCollection, 7, 2),
      description: 'recurring (2 day passes allowed)',
      type: GoalType.HeartCollection,
      targetValue: 7,
      dayPasses: 2,
      isRecurring: true,
      rewardDescription: 'Choose Movie for Ohana Movie Night'
    },
    {
      title: generateGoalTitle(GoalType.HeartCollection, 14, 4),
      description: '(4 day passes allowed)',
      type: GoalType.HeartCollection,
      targetValue: 14,
      dayPasses: 4,
      rewardDescription: 'Ohana Shave Ice'
    },
    {
      title: generateGoalTitle(GoalType.HeartCollection, 21, 5),
      description: '(5 day passes)',
      type: GoalType.HeartCollection,
      targetValue: 21,
      dayPasses: 5,
      rewardDescription: 'Choose Family Weekend Activity'
    },
    {
      title: generateGoalTitle(GoalType.HeartCollection, 30, 6),
      description: '(6 day passes)',
      type: GoalType.HeartCollection,
      targetValue: 30,
      dayPasses: 6,
      rewardDescription: 'Earn a special reward of choice'
    }
  ];

  const steps = [
    { title: 'Welcome', description: 'Welcome to Let\'s Reset!' },
    { title: `${Term} Info`, description: `${Term} name and your role` },
    { title: 'Add Children', description: `Add your children to the ${term}` },
    { title: 'Add Adults', description: 'Invite other adults (optional)' },
    { title: 'Review Goals', description: 'Review and customize goals' },
    { title: 'Complete', description: 'Complete your family setup' },
  ];

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const addChild = () => {
    if (currentChild.name.trim() && currentChild.birthMonth.trim() && currentChild.birthYear > 0) {
      const newChild = { ...currentChild };
      setChildren([...children, newChild]);
      
      // Create default goals for this child (Issue #489)
      setGoals(prevGoals => {
        const newGoals = new Map(prevGoals);
        newGoals.set(newChild.name, [...defaultGoals]);
        return newGoals;
      });
      
      setCurrentChild({
        name: '',
        aliases: [],
        birthYear: 0, // No default year - user must select
        birthMonth: '',
      });
    }
  };

  const removeChild = (index: number) => {
    const childToRemove = children[index];
    setChildren(children.filter((_, i) => i !== index));
    
    // Remove goals for this child
    setGoals(prevGoals => {
      const newGoals = new Map(prevGoals);
      newGoals.delete(childToRemove.name);
      return newGoals;
    });
  };

  const addAdult = () => {
    setAdults([...adults, { email: '', firstName: '', lastName: '' }]);
  };

  const updateAdult = (index: number, field: keyof Adult, value: string) => {
    const updatedAdults = adults.map((adult, i) => 
      i === index ? { ...adult, [field]: value } : adult
    );
    setAdults(updatedAdults);
  };

  const removeAdult = (index: number) => {
    setAdults(adults.filter((_, i) => i !== index));
  };

  const handleEditGoal = (childName: string, goalIndex: number) => {
    const childGoals = goals.get(childName);
    if (childGoals && childGoals[goalIndex]) {
      const goal = childGoals[goalIndex];
      setEditingGoal({ childName, goalIndex, goal });
      
      // Parse template data from the goal
      const heartStreakTemplate = goal.type === GoalType.HeartStreak 
        ? parseHeartStreakFromTitle(goal.title)
        : { consecutiveDays: 5, dayPasses: 1 };
      
      const starCollectionTemplate = goal.type === GoalType.StarCollection
        ? parseStarCollectionFromTitle(goal.title)
        : { totalStars: 10, timePeriodDays: 7 };
      
      const heartCollectionTemplate = goal.type === GoalType.HeartCollection
        ? parseHeartCollectionFromTitle(goal.title)
        : { totalHearts: 10, timePeriodDays: 30 };
      
      setNewGoal({
        childId: 0, // Not used in setup mode
        title: '', // Will be auto-generated, not editable
        type: goal.type,
        targetValue: goal.targetValue,
        reward: goal.rewardDescription || '', // Use the actual reward text, not description
        rewardImageUrl: undefined, // Goals in setup don't have images yet
        dayPasses: goal.type === GoalType.StarCollection || goal.type === GoalType.HeartCollection ? 0 : (goal.dayPasses ?? 0),
        isRecurring: goal.isRecurring || false,
        heartStreakTemplate,
        starCollectionTemplate,
        heartCollectionTemplate
      });
      
      setShowGoalEditModal(true);
    }
  };

  const handleSaveGoal = async () => {
    if (!editingGoal) return;
    
    // Auto-generate title based on goal type and template (same logic as GoalsManager)
    let generatedTitle = '';
    if (newGoal.type === GoalType.HeartStreak) {
      const days = newGoal.heartStreakTemplate?.consecutiveDays || 5;
      const squares = newGoal.dayPasses ?? 0;
      if (squares > 0) {
        const passText = squares === 1 ? 'day pass' : 'day passes';
        generatedTitle = `Get hearts ${days} days in a row (${squares} ${passText})`;
      } else {
        generatedTitle = `Get hearts ${days} days in a row`;
      }
    } else if (newGoal.type === GoalType.StarCollection) {
      const stars = newGoal.starCollectionTemplate?.totalStars || 10;
      const days = newGoal.starCollectionTemplate?.timePeriodDays || 0;
      
      if (days > 0) {
        generatedTitle = `Collect ${stars} stars in ${days} days`;
      } else {
        generatedTitle = `Collect ${stars} stars`;
      }
    } else if (newGoal.type === GoalType.HeartCollection) {
      const hearts = newGoal.heartCollectionTemplate?.totalHearts || 10;
      const days = newGoal.heartCollectionTemplate?.timePeriodDays || 0;
      
      if (days > 0) {
        generatedTitle = `Collect ${hearts} hearts in ${days} days`;
      } else {
        generatedTitle = `Collect ${hearts} hearts`;
      }
    }

    const updatedGoal: DefaultGoal = {
      title: generatedTitle,
      description: editingGoal.goal.description, // Keep original description (like "(2 day passes allowed)")
      type: newGoal.type,
      targetValue: newGoal.targetValue,
      dayPasses: newGoal.type === GoalType.StarCollection || newGoal.type === GoalType.HeartCollection ? 0 : (newGoal.dayPasses ?? 0),
      isRecurring: newGoal.isRecurring || false,
      rewardDescription: newGoal.reward || undefined // Store user reward in separate field
    };

    setGoals(prevGoals => {
      const newGoals = new Map(prevGoals);
      const childGoals = newGoals.get(editingGoal.childName);
      if (childGoals) {
        childGoals[editingGoal.goalIndex] = updatedGoal;
        newGoals.set(editingGoal.childName, [...childGoals]);
      }
      return newGoals;
    });
    
    setShowGoalEditModal(false);
    setEditingGoal(null);
    showSnackbar('Goal updated successfully!', 'success');
  };


  const handleSubmit = async () => {
    setIsLoading(true);
    setError('');

    try {
      // First, check if the user already has a family
      const familiesResponse = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:5001'}/api/family/list`, {
        headers: {
          'Authorization': `Bearer ${getCurrentAccessToken()}`,
        },
      });
      
      if (!familiesResponse.ok) {
        throw new Error('Failed to get family information');
      }
      
      const families = await familiesResponse.json();
      let familyId: number;
      
      if (families.length === 0) {
        // User doesn't have a family, create one
        
        const createFamilyPayload = {
          Name: familyName,
          Description: familyDescription || null,
          Timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        };
        
        const createFamilyResponse = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:5001'}/api/family`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${getCurrentAccessToken()}`,
          },
          body: JSON.stringify(createFamilyPayload),
        });
        
        if (!createFamilyResponse.ok) {
          const errorData = await createFamilyResponse.json();
          throw new Error(`Failed to create family: ${errorData.message || 'Unknown error'}`);
        }
        
        const newFamily = await createFamilyResponse.json();
        familyId = newFamily.id;
      } else {
        // User already has a family, use the existing one
        familyId = families[0].id;
      }
      
      // Save main user's role if specified
      if (userProfile.role?.trim()) {
        try {
          const userUpdateResponse = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:5001'}/api/user/profile`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${getCurrentAccessToken()}`,
            },
            body: JSON.stringify({
              Role: userProfile.role.trim(),
              ...(user?.firstName && { FirstName: user.firstName }),
              ...(user?.lastName && { LastName: user.lastName })
            }),
          });
          
          if (!userUpdateResponse.ok) {
            console.warn('Failed to update user role:', await userUpdateResponse.text());
          }
        } catch (err) {
          console.warn('Error updating user role:', err);
        }
      }
      
      // Add each child to the family (either new or existing)
      for (const child of children) {
        // Convert month name to number for API using shared utility

        const childPayload = {
          Name: child.name,
          Aliases: child.aliases || [],
          BirthYear: child.birthYear,
          BirthMonth: monthNameToNumber(child.birthMonth),
          BirthDay: child.birthDay || null,
          Gender: child.gender || null,
          // ProfileImageUrl removed - will be uploaded separately after child creation
        };


        const childResponse = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:5001'}/api/family/${familyId}/children`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${getCurrentAccessToken()}`,
          },
          body: JSON.stringify(childPayload),
        });

        if (!childResponse.ok) {
          const errorData = await childResponse.json();
          throw new Error(`Failed to add child ${child.name}: ${errorData.message || 'Unknown error'}`);
        }

        // Get the created child's ID from response
        const createdChild = await childResponse.json();
        const childId = createdChild.id || createdChild.childId;

        // Step 2: Upload avatar if one was selected (Issue #1016)
        if (child.profileImageUrl) {
          try {
            let base64ImageData: string;

            // Convert avatar to base64 depending on source
            if (child.profileImageUrl.startsWith('/static/avatars/')) {
              // Static avatar - fetch and convert to base64
              const avatarUrl = `${import.meta.env.VITE_API_URL || 'http://localhost:5001'}${child.profileImageUrl}`;
              const imageResponse = await fetch(avatarUrl);
              const blob = await imageResponse.blob();
              base64ImageData = await new Promise<string>((resolve, reject) => {
                const reader = new FileReader();
                reader.onloadend = () => resolve(reader.result as string);
                reader.onerror = reject;
                reader.readAsDataURL(blob);
              });
            } else if (child.profileImageUrl.startsWith('blob:')) {
              // Custom uploaded file - convert blob URL to base64
              const blobResponse = await fetch(child.profileImageUrl);
              const blob = await blobResponse.blob();
              base64ImageData = await new Promise<string>((resolve, reject) => {
                const reader = new FileReader();
                reader.onloadend = () => resolve(reader.result as string);
                reader.onerror = reject;
                reader.readAsDataURL(blob);
              });
            } else {
              // Already base64 or data URL
              base64ImageData = child.profileImageUrl;
            }

            // Upload avatar via API
            const avatarResponse = await fetch(
              `${import.meta.env.VITE_API_URL || 'http://localhost:5001'}/api/family/children/${childId}/avatar`,
              {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${getCurrentAccessToken()}`,
                },
                body: JSON.stringify({ imageData: base64ImageData }),
              }
            );

            if (!avatarResponse.ok) {
              console.warn('Avatar upload failed, but child was created successfully');
            }
          } catch (avatarError) {
            console.warn('Error uploading avatar for child:', avatarError);
            // Child was created successfully, so continue despite avatar failure
          }
        }

        // Create default goals for this child (Issue #489)
        const childGoals = goals.get(child.name) || defaultGoals;
        for (const goal of childGoals) {
          const goalPayload: CreateGoalRequest = {
            childId: childId,
            title: goal.title,
            type: goal.type,
            targetValue: goal.targetValue,
            rewardDescription: goal.rewardDescription || "", // Send empty string to avoid API fallback
            dayPasses: goal.dayPasses,
            isRecurring: goal.isRecurring || false
          };

          try {
            const goalResponse = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:5001'}/api/goal`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${getCurrentAccessToken()}`,
              },
              body: JSON.stringify(goalPayload),
            });

            if (!goalResponse.ok) {
              console.warn(`Failed to create goal "${goal.title}":`, await goalResponse.text());
            }
          } catch (err) {
            console.warn(`Error creating goal "${goal.title}":`, err);
          }
        }
      }
      
      // Send invitations to adults
      for (const adult of adults) {
        if (adult.email && adult.firstName && adult.lastName) {
          const adultPayload = {
            Email: adult.email,
            Role: 2, // FamilyRole.Adult enum value
            CustomRole: adult.role || undefined,
            Message: adult.customMessage || undefined,
            FirstName: adult.firstName || undefined,
            LastName: adult.lastName || undefined,
          };
          
          const adultResponse = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:5001'}/api/family/${familyId}/invite`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${getCurrentAccessToken()}`,
            },
            body: JSON.stringify(adultPayload),
          });

          if (!adultResponse.ok) {
            const errorData = await adultResponse.json();
            console.warn(`Failed to send invitation: ${errorData.message || 'Unknown error'}`);
            // Don't throw here - we want to continue even if some invitations fail
          }
        }
      }
      
      // Navigate to dashboard after successful setup
      window.location.href = '/';
    } catch (err) {
      console.error('Family setup error:', err);
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="h3" component="h1" sx={{ mb: 2, color: 'primary.main' }}>
              🌺 Let&apos;s Reset
            </Typography>

            <VideoPlayer videoFile="family-wizard-welcome.mp4" />

            <Typography variant="h4" component="h2" sx={{ mb: 3 }}>
              Welcome to Your Family&apos;s Journey!
            </Typography>
            <Typography variant="body1" sx={{ mb: 4, fontSize: '1.1rem', lineHeight: 1.6, color: 'text.secondary' }}>
              Let&apos;s Reset helps families track positive behaviors and build stronger relationships.
              Let&apos;s set up your family in just a few simple steps.
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, flexWrap: 'wrap' }}>
              {/* Chip has no Typography variant to swap to; fontSize matches h6/body1 but these are standalone Chip labels */}
              <Chip
                icon={<StarRounded />}
                label="Track positive behaviors"
                variant="outlined"
                sx={{ py: 2, px: 1, fontSize: '1rem' }}
              />
              <Chip
                icon={<FavoriteRounded />}
                label="Set and achieve goals"
                variant="outlined"
                sx={{ py: 2, px: 1, fontSize: '1rem' }}
              />
              <Chip
                icon={<GroupsRounded />}
                label="Build family connections"
                variant="outlined"
                sx={{ py: 2, px: 1, fontSize: '1rem' }}
              />
            </Box>
          </Box>
        );

      case 1:
        return (
          <Box sx={{ py: 2 }}>
            <VideoPlayer videoFile="family-wizard-family-info.mp4" />

            <Typography variant="h5" component="h2" sx={{ mb: 3, fontWeight: 600 }}>
              {Term} Information
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <TextField
                id="familyName"
                label={`${Term} Name`}
                value={familyName}
                onChange={(e) => setFamilyName(e.target.value)}
                placeholder="e.g., The Johnsons, Smith Family"
                fullWidth
                required
                variant="outlined"
                helperText="Choose a name that represents your family"
              />
              
              <Box>
                <TextField
                  id="userRole"
                  label="Your role in the family"
                  value={userProfile.role || ''}
                  onChange={(e) => setUserProfile({...userProfile, role: e.target.value})}
                  placeholder="e.g., Mom, Dad, Guardian..."
                  fullWidth
                  required
                  variant="outlined"
                  helperText="How do others in your family know you?"
                />
                
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
                  {['Mom', 'Dad', 'Guardian', 'Grandma', 'Grandpa', 'Caregiver', 'Parent', 'Step-parent'].map((role) => (
                    <Chip
                      key={role}
                      label={role}
                      onClick={() => setUserProfile({...userProfile, role})}
                      variant={userProfile.role === role ? 'filled' : 'outlined'}
                      color={userProfile.role === role ? 'primary' : 'default'}
                      sx={{ cursor: 'pointer' }}
                    />
                  ))}
                </Box>
              </Box>
            </Box>
          </Box>
        );

      case 2:
        return (
          <Box sx={{ py: 2 }}>
            <Typography variant="h5" component="h2" sx={{ mb: 3, fontWeight: 600 }}>
              Add Your Children
            </Typography>
            
            <Card sx={{ mb: 3, p: 3 }}>
              <Typography variant="h6" component="h3" sx={{ mb: 3, fontWeight: 600 }}>
                Add a Child
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <TextField
                  id="childName"
                  label="Name"
                  value={currentChild.name}
                  onChange={(e) => setCurrentChild({...currentChild, name: e.target.value})}
                  placeholder="Child's name"
                  fullWidth
                  required
                  variant="outlined"
                />
                <NicknamesChipInput
                  value={currentChild.aliases || []}
                  onChange={(aliases) => setCurrentChild({...currentChild, aliases})}
                  childName={currentChild.name}
                />
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <FormControl sx={{ minWidth: 150, flex: 1 }} variant="outlined">
                    <InputLabel id="childBirthYear-label">Birth Year</InputLabel>
                    <Select
                      labelId="childBirthYear-label"
                      id="childBirthYear"
                      value={currentChild.birthYear || ''}
                      onChange={(e) => handleChildYearChange(parseInt(String(e.target.value)) || 0)}
                      label="Birth Year"
                    >
                      {Array.from({length: 25}, (_, i) => currentYear - i).map(year => (
                        <MenuItem key={year} value={year}>{year}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl sx={{ minWidth: 150, flex: 1 }} variant="outlined" required>
                    <InputLabel id="childBirthMonth-label">Birth Month</InputLabel>
                    <Select
                      labelId="childBirthMonth-label"
                      id="childBirthMonth"
                      value={currentChild.birthMonth}
                      onChange={(e) => setCurrentChild({...currentChild, birthMonth: e.target.value})}
                      label="Birth Month"
                    >
                      {MONTH_NAMES.map((monthName, index) => {
                        const monthNum = index + 1;
                        const isDisabled = currentChild.birthYear === currentYear && monthNum > currentMonth;
                        return (
                          <MenuItem key={monthName} value={monthName} disabled={isDisabled}>
                            {monthName}
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </FormControl>
                  <FormControl sx={{ minWidth: 150, flex: 1 }} variant="outlined">
                    <InputLabel id="childGender-label">Gender (Optional)</InputLabel>
                    <Select
                      labelId="childGender-label"
                      id="childGender"
                      value={currentChild.gender || ''}
                      onChange={(e) => setCurrentChild({...currentChild, gender: e.target.value})}
                      label="Gender (Optional)"
                    >
                      <MenuItem value="">Prefer not to say</MenuItem>
                      <MenuItem value="Boy">Boy</MenuItem>
                      <MenuItem value="Girl">Girl</MenuItem>
                      <MenuItem value="Other">Other</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
                <AvatarSelector
                  selectedAvatar={currentChild.profileImageUrl}
                  onSelect={(url) => setCurrentChild({...currentChild, profileImageUrl: url})}
                />
                <Button
                  variant="contained"
                  onClick={addChild}
                  disabled={!currentChild.name.trim() || !currentChild.birthMonth.trim() || currentChild.birthYear <= 0}
                  startIcon={<PersonAddRounded />}
                >
                  Add Child
                </Button>
              </Box>
            </Card>

            {children.length > 0 && (
              <Card sx={{ p: 3 }}>
                <Typography variant="h6" component="h3" sx={{ mb: 2, fontWeight: 600 }}>
                  Your Children ({children.length})
                </Typography>
                <List>
                  {children.map((child, index) => (
                    <ListItem key={index} sx={{ mb: 1, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                      {child.profileImageUrl && (
                        <Avatar
                          src={child.profileImageUrl.startsWith('http') || child.profileImageUrl.startsWith('/')
                            ? (child.profileImageUrl.startsWith('http') ? child.profileImageUrl : `${import.meta.env.VITE_API_URL || 'http://localhost:5001'}${child.profileImageUrl}`)
                            : child.profileImageUrl}
                          sx={{ mr: 2 }}
                        />
                      )}
                      <ListItemText
                        primary={child.name}
                        secondary={`Born: ${child.birthMonth} ${child.birthYear}${child.gender ? ` • ${child.gender}` : ''}`}
                      />
                      <ListItemSecondaryAction>
                        <IconButton
                          edge="end"
                          aria-label="remove"
                          onClick={() => removeChild(index)}
                          color="error"
                        >
                          <DeleteRounded />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              </Card>
            )}
          </Box>
        );

      case 3:
        return (
          <Box sx={{ py: 2 }}>
            <VideoPlayer videoFile="family-wizard-add-adults.mp4" />

            <Typography variant="h5" component="h2" sx={{ mb: 2, fontWeight: 600 }}>
              Invite Other Adults (Optional)
            </Typography>
            <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
              Invite other family members like grandparents, caregivers, or your partner.
            </Typography>
            
            <Button
              variant="outlined"
              onClick={addAdult}
              startIcon={<PersonAddRounded />}
              sx={{ mb: 3 }}
            >
              Add Adult
            </Button>

            {adults.map((adult, index) => (
              <Card key={index} sx={{ mb: 2, p: 3 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    <Typography variant="h6" sx={{ flex: 1 }}>
                      Adult {index + 1}
                    </Typography>
                    <IconButton
                      onClick={() => removeAdult(index)}
                      color="error"
                      aria-label="Remove adult"
                    >
                      <DeleteRounded />
                    </IconButton>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <TextField
                      label="First Name"
                      value={adult.firstName}
                      onChange={(e) => updateAdult(index, 'firstName', e.target.value)}
                      placeholder="First name"
                      sx={{ flex: 1, minWidth: 150 }}
                      variant="outlined"
                    />
                    <TextField
                      label="Last Name"
                      value={adult.lastName}
                      onChange={(e) => updateAdult(index, 'lastName', e.target.value)}
                      placeholder="Last name"
                      sx={{ flex: 1, minWidth: 150 }}
                      variant="outlined"
                    />
                  </Box>
                  <TextField
                    label="Email"
                    type="email"
                    value={adult.email}
                    onChange={(e) => updateAdult(index, 'email', e.target.value)}
                    placeholder="email@example.com"
                    fullWidth
                    variant="outlined"
                  />
                  <Box>
                    <TextField
                      fullWidth
                      label="Role"
                      value={adult.role || ''}
                      onChange={(e) => updateAdult(index, 'role', e.target.value)}
                      placeholder="e.g., Mom, Dad, Grandma..."
                      variant="outlined"
                      helperText="How do others in your family know them?"
                    />
                    
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
                      {['Mom', 'Dad', 'Guardian', 'Grandma', 'Grandpa', 'Caregiver', 'Parent', 'Step-parent', 'Uncle', 'Aunt'].map((role) => (
                        <Chip
                          key={role}
                          label={role}
                          onClick={() => updateAdult(index, 'role', role)}
                          variant={adult.role === role ? 'filled' : 'outlined'}
                          color={adult.role === role ? 'primary' : 'default'}
                          sx={{ cursor: 'pointer' }}
                        />
                      ))}
                    </Box>
                  </Box>
                  <TextField
                    label="Custom Message (Optional)"
                    value={adult.customMessage || ''}
                    onChange={(e) => updateAdult(index, 'customMessage', e.target.value)}
                    placeholder="Personal message for the invitation..."
                    fullWidth
                    multiline
                    rows={2}
                    variant="outlined"
                  />
                </Box>
              </Card>
            ))}
          </Box>
        );

      case 4:
        return (
          <Box sx={{ py: 2 }}>
            <VideoPlayer videoFile="family-wizard-review-goals.mp4" />

            <Typography variant="h5" component="h2" sx={{ mb: 3, fontWeight: 600 }}>
              Review and Customize Goals
            </Typography>
            <Typography variant="body1" sx={{ mb: 4, color: 'text.secondary' }}>
              We&apos;ve created default goals for each of your children. You can customize them to fit your family&apos;s needs.
            </Typography>
            
            {children.map((child, childIndex) => (
              <Card key={childIndex} sx={{ mb: 3, p: 3 }}>
                <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                  Goals for {child.name}
                </Typography>
                
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {(goals.get(child.name) || []).map((goal, goalIndex) => (
                    <Box
                      key={goalIndex}
                      sx={{
                        p: 2,
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 2,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}
                    >
                      <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          {goal.title}
                        </Typography>
                        {goal.rewardDescription && (
                          <Typography variant="body2" color="success.main">
                            🎁 Reward: {goal.rewardDescription}
                          </Typography>
                        )}
                      </Box>
                      <Button
                        variant="outlined"
                        size="small"
                        sx={{ minWidth: 'auto' }}
                        onClick={() => handleEditGoal(child.name, goalIndex)}
                      >
                        Edit
                      </Button>
                    </Box>
                  ))}
                </Box>
              </Card>
            ))}
          </Box>
        );

      case 5:
        return (
          <Box sx={{ py: 2 }}>
            <Typography variant="h5" component="h2" sx={{ mb: 3, fontWeight: 600 }}>
              Complete Your {Term} Setup
            </Typography>
            <Card sx={{ mb: 3, p: 3 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                    Family Name
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {familyName}
                  </Typography>
                </Box>
                
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                    Family Members
                  </Typography>
                  <List dense>
                    {/* Main User */}
                    <ListItem>
                      <ListItemText
                        primary={`${user?.firstName || 'You'} ${user?.lastName || ''}`}
                        secondary={`Role: ${userProfile.role || 'Not specified'} • Primary Account`}
                      />
                    </ListItem>
                    
                    {/* Adults */}
                    {adults.map((adult, index) => (
                      <ListItem key={`adult-${index}`}>
                        <ListItemText
                          primary={`${adult.firstName} ${adult.lastName}`}
                          secondary={`Role: ${adult.role || 'Not specified'} • Email: ${adult.email}`}
                        />
                      </ListItem>
                    ))}
                    
                    {/* Children */}
                    {children.map((child, index) => (
                      <ListItem key={`child-${index}`}>
                        {child.profileImageUrl && (
                          <Avatar
                            src={child.profileImageUrl.startsWith('http') || child.profileImageUrl.startsWith('/')
                              ? (child.profileImageUrl.startsWith('http') ? child.profileImageUrl : `${import.meta.env.VITE_API_URL || 'http://localhost:5001'}${child.profileImageUrl}`)
                              : child.profileImageUrl}
                            sx={{ mr: 2 }}
                          />
                        )}
                        <ListItemText
                          primary={child.name}
                          secondary={`Child • Born: ${child.birthMonth} ${child.birthYear}${child.gender ? ` • ${child.gender}` : ''}`}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                    Goals Summary
                  </Typography>
                  {children.map((child, index) => (
                    <Box key={index} sx={{ mb: 2 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        Goals for {child.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {(goals.get(child.name) || []).map((goal, goalIndex) => (
                          <span key={goalIndex}>
                            • {goal.title}
                            {goal.rewardDescription && ` - Reward: ${goal.rewardDescription}`}
                            {goalIndex < (goals.get(child.name) || []).length - 1 && <br/>}
                          </span>
                        ))}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Card>
            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}
            <Button
              variant="contained"
              size="large"
              onClick={handleSubmit}
              disabled={isLoading || !familyName.trim() || children.length === 0}
              startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : undefined}
              sx={{ py: 1.5, px: 4 }}
            >
              {isLoading ? 'Creating Family...' : 'Complete Setup'}
            </Button>
          </Box>
        );

      default:
        return null;
    }
  };

  if (!user) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">
          <Typography variant="h6" component="h2" sx={{ mb: 1 }}>
            Authentication Required
          </Typography>
          <Typography variant="body1">
            Please log in to set up your family.
          </Typography>
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        {/* Stepper Header */}
        <Box sx={{ mb: 4 }}>
          <Stepper
            activeStep={currentStep}
            orientation={isMobile ? 'vertical' : 'horizontal'}
            alternativeLabel={!isMobile}
          >
            {steps.map((step) => (
              <Step key={step.title}>
                <StepLabel>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    {step.title}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {step.description}
                  </Typography>
                </StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>

        {/* Step Content */}
        <Box sx={{ minHeight: 400, mb: 4 }}>
          {renderStepContent()}
        </Box>

        {/* Navigation Footer */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {currentStep > 0 ? (
            <Button
              variant="outlined"
              onClick={handlePrevious}
              disabled={isLoading}
              startIcon={<NavigateBeforeRounded />}
            >
              Previous
            </Button>
          ) : (
            <Box /> // Spacer
          )}
          {currentStep < steps.length - 1 && (
            <Button
              variant="contained"
              onClick={handleNext}
              disabled={
                isLoading ||
                (currentStep === 1 && (!familyName.trim() || !userProfile.role?.trim())) ||
                (currentStep === 2 && children.length === 0)
              }
              endIcon={<NavigateNextRounded />}
            >
              Next
            </Button>
          )}
        </Box>
      </Paper>

      {/* User Role Selection Modal */}
      <RoleEditModal
        open={showUserRoleModal}
        onClose={() => setShowUserRoleModal(false)}
        onSave={(newRole) => {
          setUserProfile({ ...userProfile, role: newRole });
          setShowUserRoleModal(false);
        }}
        currentRole={userProfile.role || ''}
        userId={user?.userId || 0}
        familyId={0} // Not applicable during setup
        isAdmin={false}
        isPrimaryCaregiver={false}
      />

      {/* Adult Role Selection Modal */}
      <RoleEditModal
        open={showAdultRoleModal}
        onClose={() => {
          setShowAdultRoleModal(false);
          setEditingAdultIndex(-1);
        }}
        onSave={(newRole) => {
          if (editingAdultIndex >= 0) {
            updateAdult(editingAdultIndex, 'role', newRole);
          }
          setShowAdultRoleModal(false);
          setEditingAdultIndex(-1);
        }}
        currentRole={editingAdultIndex >= 0 ? (adults[editingAdultIndex]?.role || '') : ''}
        userId={user?.userId || 0}
        familyId={0} // Not applicable during setup
        isAdmin={false}
        isPrimaryCaregiver={false}
      />

      {/* Goal Edit Modal - Using existing GoalsManager pattern */}
      <Dialog 
        open={showGoalEditModal} 
        onClose={() => {}} // Prevent closing on backdrop click or escape key
        maxWidth="sm" 
        fullWidth
        disableEscapeKeyDown
      >
        <DialogTitle>Edit Goal</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Stack spacing={3}>
              <Box>
                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                  Child
                </Typography>
                <TextField
                  value={editingGoal?.childName || ''}
                  disabled
                  fullWidth
                  variant="outlined"
                  helperText="Child cannot be changed during setup"
                />
              </Box>
              
              {/* Goal Title field removed per specification - titles are auto-generated */}
              
              <FormControl fullWidth>
                <InputLabel>Goal Type</InputLabel>
                <Select
                  value={newGoal.type}
                  onChange={(e) => {
                    const newType = e.target.value as GoalType;
                    let newTargetValue = newGoal.targetValue;
                    
                    // Update targetValue to match the template when switching goal types
                    if (newType === GoalType.HeartStreak) {
                      newTargetValue = newGoal.heartStreakTemplate?.consecutiveDays || 5;
                    } else if (newType === GoalType.StarCollection) {
                      newTargetValue = newGoal.starCollectionTemplate?.totalStars || 10;
                    } else if (newType === GoalType.HeartCollection) {
                      newTargetValue = newGoal.heartCollectionTemplate?.totalHearts || 10;
                    }
                    
                    setNewGoal({ ...newGoal, type: newType, targetValue: newTargetValue });
                  }}
                  label="Goal Type"
                >
                  <MenuItem value={GoalType.HeartStreak}>Heart Streak</MenuItem>
                  <MenuItem value={GoalType.StarCollection}>Star Collection</MenuItem>
                  <MenuItem value={GoalType.HeartCollection}>Heart Collection</MenuItem>
                </Select>
              </FormControl>
              
              {/* Dynamic Goal Type Fields */}
              <Box data-testid="dynamic-fields">
                <Fade in={true} timeout={300}>
                  <Box>
                    {newGoal.type === GoalType.HeartStreak && (
                      <Stack spacing={2}>
                        <Typography variant="subtitle2" color="primary">
                          Heart Streak Settings
                        </Typography>
                        <TextField
                          label="Consecutive Days (2-30)"
                          type="number"
                          inputProps={{ min: 2, max: 30 }}
                          value={newGoal.heartStreakTemplate?.consecutiveDays ?? 5}
                          onChange={(e) => {
                            const consecutiveDays = parseInt(e.target.value) || 2;
                            setNewGoal({ 
                              ...newGoal,
                              targetValue: consecutiveDays,
                              heartStreakTemplate: {
                                ...newGoal.heartStreakTemplate!,
                                consecutiveDays: consecutiveDays
                              }
                            });
                          }}
                          required
                          fullWidth
                        />
                        <TextField
                          label="Day Passes (0+)"
                          type="number"
                          inputProps={{ min: 0 }}
                          value={newGoal.dayPasses ?? 0}
                          onChange={(e) => {
                            const value = e.target.value === '' ? 0 : parseInt(e.target.value);
                            const clampedValue = isNaN(value) ? 0 : Math.max(value, 0);
                            setNewGoal({ 
                              ...newGoal, 
                              dayPasses: clampedValue
                            });
                          }}
                          required
                          fullWidth
                        />
                      </Stack>
                    )}
                    
                    {newGoal.type === GoalType.StarCollection && (
                      <Stack spacing={2}>
                        <Typography variant="subtitle2" color="primary">
                          Star Collection Settings
                        </Typography>
                        <TextField
                          label="Total Stars to Collect (1+)"
                          type="number"
                          inputProps={{ min: 1 }}
                          value={newGoal.starCollectionTemplate?.totalStars ?? 10}
                          onChange={(e) => {
                            const totalStars = parseInt(e.target.value) || 1;
                            setNewGoal({ 
                              ...newGoal,
                              targetValue: totalStars,
                              starCollectionTemplate: {
                                ...newGoal.starCollectionTemplate!,
                                totalStars: totalStars
                              }
                            });
                          }}
                          required
                          fullWidth
                        />
                        <TextField
                          label="Time Period in Days"
                          type="number"
                          placeholder="No limit"
                          value={newGoal.starCollectionTemplate?.timePeriodDays === 0 ? '' : (newGoal.starCollectionTemplate?.timePeriodDays ?? '')}
                          onChange={(e) => {
                            const value = e.target.value;
                            const numValue = parseInt(value);
                            
                            if (numValue === 0) {
                              setNewGoal({ 
                                ...newGoal, 
                                starCollectionTemplate: {
                                  ...newGoal.starCollectionTemplate!,
                                  timePeriodDays: 0
                                }
                              });
                              e.target.value = '';
                              return;
                            }
                            
                            setNewGoal({ 
                              ...newGoal, 
                              starCollectionTemplate: {
                                ...newGoal.starCollectionTemplate!,
                                timePeriodDays: value === '' ? 0 : (numValue > 0 ? numValue : 0)
                              }
                            });
                          }}
                          fullWidth
                        />
                      </Stack>
                    )}
                    
                    {newGoal.type === GoalType.HeartCollection && (
                      <Stack spacing={2}>
                        <Typography variant="subtitle2" color="primary">
                          Heart Collection Settings
                        </Typography>
                        <TextField
                          label="Total Hearts to Collect (1+)"
                          type="number"
                          inputProps={{ min: 1 }}
                          value={newGoal.heartCollectionTemplate?.totalHearts ?? 10}
                          onChange={(e) => {
                            const totalHearts = parseInt(e.target.value) || 1;
                            setNewGoal({ 
                              ...newGoal,
                              targetValue: totalHearts,
                              heartCollectionTemplate: {
                                ...newGoal.heartCollectionTemplate!,
                                totalHearts: totalHearts
                              }
                            });
                          }}
                          required
                          fullWidth
                        />
                        <TextField
                          label="Time Period in Days"
                          type="number"
                          placeholder="No limit"
                          value={newGoal.heartCollectionTemplate?.timePeriodDays === 0 ? '' : (newGoal.heartCollectionTemplate?.timePeriodDays ?? '')}
                          onChange={(e) => {
                            const value = e.target.value;
                            const numValue = parseInt(value);
                            
                            if (numValue === 0) {
                              setNewGoal({ 
                                ...newGoal, 
                                heartCollectionTemplate: {
                                  ...newGoal.heartCollectionTemplate!,
                                  timePeriodDays: 0
                                }
                              });
                              e.target.value = '';
                              return;
                            }
                            
                            setNewGoal({ 
                              ...newGoal, 
                              heartCollectionTemplate: {
                                ...newGoal.heartCollectionTemplate!,
                                timePeriodDays: value === '' ? 0 : (numValue > 0 ? numValue : 0)
                              }
                            });
                          }}
                          fullWidth
                        />
                      </Stack>
                    )}
                  </Box>
                </Fade>
              </Box>
              
              <Box>
                <TextField
                  label="Reward (Optional)"
                  value={newGoal.reward}
                  onChange={(e) => setNewGoal({ ...newGoal, reward: e.target.value })}
                  placeholder="e.g., 'Extra 30 minutes screen time'"
                  fullWidth
                />
              </Box>
              
              {/* Recurring Goal Checkbox */}
              <Box sx={{ mt: 2 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={newGoal.isRecurring || false}
                      onChange={(e) => setNewGoal({ ...newGoal, isRecurring: e.target.checked })}
                      color="primary"
                    />
                  }
                  label="Set as recurring"
                />
                <Typography variant="body2" color="text.secondary" sx={{ ml: 4, mt: 0.5 }}>
                  Goal will automatically recreate when completed or failed
                </Typography>
              </Box>
            </Stack>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setShowGoalEditModal(false);
              setEditingGoal(null);
            }}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button 
            variant="contained"
            onClick={handleSaveGoal}
          >
            Update Goal
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default FamilySetupWizard;